import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../shadcn/ui/dialog";
import { Button } from "../../shadcn/ui/button";
import { Input } from "../../shadcn/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../shadcn/ui/table";
import { Tabs, TabsList, TabsTrigger } from "../../shadcn/ui/tabs";
import { FaSearch, FaPlus, FaEdit, FaTrash, FaEye } from "react-icons/fa";
import SortDropdown from "../../components/SortDropdown";
import * as localService from "../../services/localService";
import { organizationApiService } from "../../services/apiV2Service";
import { getCurrentUserOrganizationId } from "../host-experience/host-clients/utils/hostClientsUtils";
import billingService from "../../services/billingService";
import moment from "moment-timezone";
import Pagination from "../../components/Pagination";
import { useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import warning from "../../img/Web/Spacia/t-warning 3.svg";
import { BlockerModal } from "../../components/FeedbackModall";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { TextField } from "@mui/material";
import logger from "../../utils/logger";
import LoadingSpinner from "../../components/LoadingSpinner";
import toastUtils from "../../utils/toast";
import { showApiMessage } from "../../utils/messageHandler";

const Orders = () => {
  // Modal states
  const [show, setShow] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showEditInvoiceModal, setShowEditInvoiceModal] = useState(false);
  const [showInvoiceDetailsModal, setShowInvoiceDetailsModal] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [hasNoMobileWalletAccount, setHasNoMobileWalletAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const handleClose = () => setShow(false);

  // Pagination and data states
  const [totalPageSize, setTotalPageSize] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [allUnpaidInvoices, setAllUnpaidInvoices] = useState([]);
  const [makeRequestForUnpaidInvoices, setMakeRequestForUnpaidInvoices] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedInvoices, setSelectedInvoices] = useState(null);
  const [currency, setCurrency] = useState("");

  // Invoice states
  const [invoices, setInvoices] = useState([]);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    description: "",
    currencyId: 1, // Default to USD
    invoiceItems: [
      {
        description: "",
        quantity: 1,
        unitPrice: "",
      },
    ],
    productType: "SUBSCRIPTION",
    serviceDate: new Date().toISOString().split("T")[0],
    servicePackageId: "",
    organizationId: undefined,
  });

  // Date and search states
  const [selectedMonthAndYear, setSelectedMonthAndYear] = useState(new Date());
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [currencies, setCurrencies] = useState([]);
  const [sortBy, setSortBy] = useState("createdAt:desc");

  const ORDERS_SORT_OPTIONS = [{ field: "createdAt", label: "Created" }];

  const history = useHistory();

  useEffect(() => {
    setCurrency(localService.getCurrency());
  }, []);

  // Platform admin detection
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  // Tab state for filtering invoices by organization type
  const [activeTab, setActiveTab] = useState("all"); // "all", "hosts", "b2b"

  useEffect(() => {
    const user = localService.getUser();
    const platformFlag = user?.platformUser === true || user?.organization?.type?.admin === true;
    setIsPlatformAdmin(!!platformFlag);
  }, []);

  // Load currencies for selection in create invoice
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        const response = await billingService.getCurrencies();
        const list = Array.isArray(response?.data?.data) ? response.data.data : response?.data;
        if (Array.isArray(list)) {
          setCurrencies(list);
          const defaultCurrency = list.find(c => c.isDefault);
          if (defaultCurrency?.id) {
            setInvoiceForm(prev => ({
              ...prev,
              currencyId: defaultCurrency.id,
            }));
          }
        }
      } catch (err) {
        logger.error("Failed to load currencies:", err);
      }
    };
    loadCurrencies();
  }, []);

  // Fetch invoices using billing service
  const fetchInvoices = async (page = 0, search = "") => {
    try {
      setLoading(true);
      const currentUser = localService.getUser();
      const orgId = currentUser?.organization?.id;
      const platformFlag = currentUser?.platformUser === true || currentUser?.organization?.type?.admin === true;

      const params = {
        page: page + 1, // API uses 1-based pagination
        limit: itemsPerPage,
        orderBy: sortBy.replace(":", ":"),
        includeRelations: true,
        ...(search && { referenceCode: search }),
      };

      // For platform admin, omit organizationId to fetch all invoices
      if (!platformFlag) {
        if (!orgId) {
          toastUtils.error("Organization not found", {
            description: "Please ensure you are logged in with a valid organization.",
          });
          setLoading(false);
          return;
        }
        params.organizationId = orgId;
      }

      // Add organizationTypeId filter for platform admin based on active tab
      if (platformFlag && activeTab !== "all") {
        // org type ID 2 = HOST_PROPERTY_DISTRIBUTION, 3 = HOST_PROPERTY_MANAGEMENT
        // These represent "Host SaaS users"
        if (activeTab === "hosts") {
          // For hosts, fetch both type 2 and 3 separately
          const [response2, response3] = await Promise.all([
            billingService.getInvoices({ ...params, organizationTypeId: 2 }),
            billingService.getInvoices({ ...params, organizationTypeId: 3 }),
          ]);

          const data2 = response2.data?.data || response2.data || [];
          const data3 = response3.data?.data || response3.data || [];

          // Merge and deduplicate by invoice ID
          const mergedData = [...data2, ...data3];
          const uniqueInvoices = mergedData.filter(
            (invoice, index, self) => index === self.findIndex(i => i.id === invoice.id)
          );

          setInvoices(uniqueInvoices);
          setTotalPageSize(uniqueInvoices.length);
          setItemsPerPage(itemsPerPage);
          setLoading(false);
          return;
        } else if (activeTab === "b2b") {
          // B2B customers - you'll need to determine the correct type IDs
          // For now, we'll handle this on the client side or fetch all and filter
        }
      }

      const response = await billingService.getInvoices(params);

      if (response.status === 200) {
        const data = response.data?.data || response.data || [];
        setInvoices(data);
        setTotalPageSize(response.data?.totalCount || data.length);
        setItemsPerPage(response.data?.pageSize || itemsPerPage);
      }
    } catch (error) {
      logger.error("Error fetching invoices:", error);
      showApiMessage(error, "Load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(0, searchValue);
  }, [activeTab, sortBy]); // Refetch when tab or sort changes

  // Mobile wallet accounts functionality removed - not needed for invoice management

  const formatDateToString = date => {
    let dateStr = null;

    if (date) {
      logger.log("about to format date: " + date + " to 'dd/MM/yyyy'");

      try {
        dateStr = moment(date).format("DD/MM/yyyy");
      } catch (e) {
        logger.log("an error occurred while formatting date to 'dd/MM/yyyy' string");
      }
    }
    return dateStr;
  };

  const handleMonthAndYearChange = value => {
    logger.log("month / year value is:", value);

    const date = new Date(value);
    const year = date.getFullYear(); // get date full year
    const month = date.getMonth(); // get month

    const startOfMonth = formatDateToString(new Date(year, month, 1));
    const endOfMoth = formatDateToString(new Date(year, month + 1, 0));

    logger.log("start of month:", startOfMonth);
    logger.log("end of month:", endOfMoth);

    setSelectedMonthAndYear(value);
  };

  const viewPayment = _token => {
    toastUtils.info("Invoice download feature coming soon", {
      description: "This feature will be implemented using the billing service.",
    });
  };

  const changePage = selected => {
    const newPage = selected.selected + 1;
    setCurrentPage(newPage);
    fetchInvoices(selected.selected, searchValue);
  };

  function performSearch(e) {
    e.preventDefault();
    setCurrentPage(1);
    fetchInvoices(0, searchValue);
  }

  const showMakePaymentPage = () => {
    history.push({
      pathname: "/make-payment",
      state: {
        invoiceAmount: totalAmount,
        selectedInvoices,
      },
    });
  };

  useEffect(() => {
    if (show) {
      setMakeRequestForUnpaidInvoices(true);

      // TODO: Implement unpaid invoices using billing service
      // For now, we'll use the existing invoices and filter for unpaid ones
      const unpaidInvoices = invoices.filter(invoice => invoice.status === "PENDING");
      setAllUnpaidInvoices(unpaidInvoices);
      setMakeRequestForUnpaidInvoices(false);
    }
  }, [show, invoices]);

  // Invoice CRUD operations
  const handleCreateInvoice = async () => {
    try {
      const currentUser = localService.getUser();
      const orgId = currentUser?.organization?.id;
      const platformFlag = currentUser?.platformUser === true || currentUser?.organization?.type?.admin === true;

      // For platform admin, require selecting a customer organization (not platform org)
      if (platformFlag) {
        if (!invoiceForm.organizationId) {
          toastUtils.error("Customer organization required", {
            description: "Please select a customer to assign the invoice to.",
          });
          return;
        }
        if (orgId && invoiceForm.organizationId === orgId) {
          toastUtils.error("Invalid organization", {
            description: "Admin cannot create invoices for the platform organization.",
          });
          return;
        }
      }
      // For non-platform users, pin to their own organization
      if (!platformFlag && !orgId) {
        toastUtils.error("Organization not found");
        return;
      }

      const payload = {
        organizationId: platformFlag ? invoiceForm.organizationId : orgId,
        clientReference: `${platformFlag ? invoiceForm.organizationId : orgId}00${uuidv4().replace(/-/g, "")}`,
        description: invoiceForm.description,

        currencyId: invoiceForm.currencyId,
        invoiceItems: invoiceForm.invoiceItems
          .filter(item => item.description && item.quantity && item.unitPrice)
          .map(item => {
            const qtyNum = Number(item.quantity);
            const priceNum = Number(item.unitPrice);
            const amountNum = qtyNum * priceNum;
            return {
              description: item.description,
              quantity: qtyNum,
              unitPrice: String(priceNum.toFixed ? priceNum.toFixed(2) : priceNum),
              amount: String(amountNum.toFixed ? amountNum.toFixed(2) : amountNum),
            };
          }),
        productType: invoiceForm.productType,
        serviceDate: invoiceForm.serviceDate,
        servicePackageId: invoiceForm.servicePackageId || undefined,
      };

      const response = await billingService.createInvoice(payload);

      if (response.status === 201) {
        toastUtils.success("Invoice created successfully!");
        setShowInvoiceModal(false);
        setInvoiceForm({
          description: "",
          currencyId: 1,
          invoiceItems: [{ description: "", quantity: 1, unitPrice: "" }],
          productType: "SUBSCRIPTION",
          serviceDate: new Date().toISOString().split("T")[0],
          servicePackageId: "",
          organizationId: undefined,
        });
        fetchInvoices(currentPage - 1, searchValue);
      }
    } catch (error) {
      logger.error("Error creating invoice:", error);
      showApiMessage(error, "Create invoice");
    }
  };

  // Organization search for platform admin
  const [organizationSearchTerm, setOrganizationSearchTerm] = useState("");
  const [orgSearchResults, setOrgSearchResults] = useState([]);
  const [orgSearching, setOrgSearching] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);

  const searchOrganizations = async term => {
    if (!term || term.length < 2) {
      setOrgSearchResults([]);
      return;
    }
    try {
      setOrgSearching(true);
      const currentOrgId = getCurrentUserOrganizationId();
      const res = await organizationApiService.searchOrganizations({
        q: term,
        limit: 10,
      });
      const list = res?.data?.data;
      // Filter out current user's organization in UI
      const filteredList = Array.isArray(list) ? list.filter(org => org.id !== currentOrgId) : [];
      setOrgSearchResults(filteredList);
    } catch (err) {
      logger.error("Error searching organizations:", err);
      setOrgSearchResults([]);
    } finally {
      setOrgSearching(false);
    }
  };

  const handleUpdateInvoice = async () => {
    try {
      if (!editingInvoice) return;

      const payload = {
        description: invoiceForm.description || "",
      };

      const response = await billingService.updateInvoice(editingInvoice.id, payload);

      if (response.status === 200) {
        toastUtils.success("Invoice updated successfully!");
        setShowEditInvoiceModal(false);
        setEditingInvoice(null);
        fetchInvoices(currentPage - 1, searchValue);
      }
    } catch (error) {
      logger.error("Error updating invoice:", error);
      showApiMessage(error, "Update invoice");
    }
  };

  const handleDeleteInvoice = invoice => {
    setInvoiceToDelete(invoice);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteInvoice = async () => {
    if (!invoiceToDelete) return;

    try {
      const currentUser = localService.getUser();
      const invoiceAmount =
        invoiceToDelete.invoiceItems?.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) || 0;

      const payload = {
        reference: invoiceToDelete.referenceCode || `INV-${invoiceToDelete.id}`,
        narration: "Invoice cancelled by administrator",
      };

      const response = await billingService.cancelInvoice(payload);

      if (response.status === 200 || response.status === 201) {
        toastUtils.success("Invoice cancelled successfully!");
        fetchInvoices(currentPage - 1, searchValue);
      }
    } catch (error) {
      logger.error("Error cancelling invoice:", error);
      showApiMessage(error, "Cancel invoice");
    } finally {
      setShowDeleteConfirm(false);
      setInvoiceToDelete(null);
    }
  };

  const handleViewInvoice = async invoiceId => {
    setShowInvoiceDetailsModal(true);
    setDetailsLoading(true);
    setInvoiceDetails(null);
    try {
      const res = await billingService.getInvoiceById(invoiceId);
      setInvoiceDetails(res?.data?.data || res?.data);
    } catch (error) {
      showApiMessage(error, "Load invoice");
    } finally {
      setDetailsLoading(false);
    }
  };

  const openEditInvoiceModal = invoice => {
    setEditingInvoice(invoice);
    setInvoiceForm({
      ...invoiceForm,
      description: invoice.description || "",
    });
    setShowEditInvoiceModal(true);
  };

  const addInvoiceItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      invoiceItems: [...invoiceForm.invoiceItems, { amount: "", description: "" }],
    });
  };

  const removeInvoiceItem = index => {
    const newItems = invoiceForm.invoiceItems.filter((_, i) => i !== index);
    setInvoiceForm({
      ...invoiceForm,
      invoiceItems: newItems.length > 0 ? newItems : [{ amount: "", description: "" }],
    });
  };

  const updateInvoiceItem = (index, field, value) => {
    const newItems = [...invoiceForm.invoiceItems];
    newItems[index] = { ...newItems[index], [field]: value };
    setInvoiceForm({
      ...invoiceForm,
      invoiceItems: newItems,
    });
  };

  const renderResultForUnpaidInvoices = () => {
    let whatToRender = "";

    if (makeRequestForUnpaidInvoices) {
      whatToRender = <LoadingSpinner loading={true} />;
    } else if (!makeRequestForUnpaidInvoices && allUnpaidInvoices.length === 0) {
      whatToRender = (
        <div className="flex justify-center items-center py-16">
          <p className="text-xl font-bold text-gray-600">No Unpaid Invoices</p>
        </div>
      );
    } else if (!makeRequestForUnpaidInvoices && allUnpaidInvoices.length > 0) {
      whatToRender = (
        <div className="overflow-x-auto">
          <table className="w-full min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Number
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Value
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Date
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Check All &nbsp;
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    onChange={e => {
                      const isChecked = e.currentTarget.checked;
                      logger.log("is checked?", isChecked);

                      // select all checkboxes
                      const allInvoiceCheckboxes = document.querySelectorAll(".invoice-check");
                      allInvoiceCheckboxes.forEach(checkbox => {
                        checkbox.checked = isChecked;
                      });

                      if (isChecked) {
                        const selectedInvoicesAmount = allUnpaidInvoices
                          .map(o => o.netAmount)
                          .reduce((val1, val2) => val1 + val2, 0);
                        logger.log("selected invoices amount:", selectedInvoicesAmount);
                        setTotalAmount(selectedInvoicesAmount);
                        setSelectedInvoices(allUnpaidInvoices);
                      } else {
                        setTotalAmount(0);
                        setSelectedInvoices(null);
                      }
                    }}
                  />
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {allUnpaidInvoices.map((unpaidInvoice, index) => {
                const amount = unpaidInvoice.netAmount.toLocaleString();
                const invoiceValue = `${currency} ${amount}`;

                return (
                  <tr key={unpaidInvoice.referenceCode || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{invoiceValue}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {moment(unpaidInvoice.createdOn).format("DD-MMM-YYYY")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <input
                        type="checkbox"
                        className="invoice-check h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        onChange={e => {
                          allUnpaidInvoices.find(o => o.referenceCode === unpaidInvoice.referenceCode).checked =
                            e.target.checked;
                          const selectedInvoicesAmount = allUnpaidInvoices
                            .filter(o => o.checked === true)
                            .map(o => o.netAmount)
                            .reduce((val1, val2) => val1 + val2, 0);
                          logger.log("selected invoices amount:", selectedInvoicesAmount);
                          setTotalAmount(selectedInvoicesAmount);

                          const selectedInvoices = allUnpaidInvoices.filter(o => o.checked === true).map(o => o);
                          setSelectedInvoices(selectedInvoices);
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    return whatToRender;
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h4 className="text-xl font-bold">Invoices</h4>
            <p className="text-gray-600">Manage your organization invoices</p>
          </div>

          <div className="w-full md:w-auto flex-grow">
            <form className="flex gap-2" onSubmit={performSearch}>
              <div className="relative flex-1">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by reference code..."
                  type="search"
                  value={searchValue}
                  onChange={e => setSearchValue(e.target.value)}
                  className="!pl-10"
                />
              </div>
              <Button type="submit" variant="outline" size="icon">
                <FaSearch className="w-4 h-4" />
              </Button>
            </form>
          </div>

          <div className="flex space-x-3">
            <SortDropdown options={ORDERS_SORT_OPTIONS} value={sortBy} onChange={setSortBy} />
            <button
              type="button"
              onClick={() => setShowInvoiceModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              <FaPlus className="inline-block mr-2" />
              Create Invoice
            </button>
            {/* <button
              type='button'
              onClick={() => setShowStatementModal(true)}
              className='bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors'
            >
              <FaDownload className='inline-block mr-2' />
              Download Statement
            </button> */}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
        {/* Tabs for filtering by organization type (only for platform admin) */}
        {isPlatformAdmin && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="all">All Invoices</TabsTrigger>
              <TabsTrigger value="hosts">Client Invoices</TabsTrigger>
              <TabsTrigger value="b2b">Platform Invoices</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        <h6 className="font-bold mb-4">Invoice History</h6>

        <LoadingSpinner loading={loading} />
        {!loading && (
          <>
            {/* Desktop View */}
            <div className="hidden md:block overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice, index) => {
                    const getStatusColor = status => {
                      if (!status) return "bg-gray-200 text-gray-700";

                      switch (status.toUpperCase()) {
                        case "PENDING":
                          return "bg-yellow-200 text-yellow-700";
                        case "PAID":
                          return "bg-green-200 text-green-700";
                        case "CANCELLED":
                          return "bg-red-200 text-red-700";
                        default:
                          return "bg-gray-200 text-gray-700";
                      }
                    };

                    const totalAmount =
                      invoice.invoiceItems?.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) || 0;

                    return (
                      <TableRow key={invoice.id || index}>
                        <TableCell>{moment(invoice.createdAt || invoice.serviceDate).format("DD-MMM-YYYY")}</TableCell>
                        <TableCell>
                          <span className="font-medium">{invoice.referenceCode || `INV-${invoice.id}`}</span>
                        </TableCell>
                        <TableCell>{invoice.description || "Invoice"}</TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {invoice.currency?.code || "KES"} {totalAmount.toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${getStatusColor(invoice.status)}`}
                          >
                            {invoice.status || "PENDING"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <button
                              className="text-blue-500 hover:text-blue-700"
                              onClick={() => handleViewInvoice(invoice.id)}
                              title="View Invoice"
                            >
                              <FaEye className="w-4 h-4" />
                            </button>
                            <button
                              className="text-green-500 hover:text-green-700"
                              onClick={() => openEditInvoiceModal(invoice)}
                              title="Edit Invoice"
                            >
                              <FaEdit className="w-4 h-4" />
                            </button>
                            {invoice.status !== "CANCELLED" && (
                              <button
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDeleteInvoice(invoice)}
                                title="Cancel Invoice"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Empty state */}
                  {invoices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No invoices found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile View */}
            <div className="md:hidden space-y-4">
              {invoices.length > 0 ? (
                invoices.map((invoice, index) => {
                  const getStatusColor = status => {
                    if (!status) return "bg-gray-200 text-gray-700";

                    switch (status.toUpperCase()) {
                      case "PENDING":
                        return "bg-yellow-200 text-yellow-700";
                      case "PAID":
                        return "bg-green-200 text-green-700";
                      case "CANCELLED":
                        return "bg-red-200 text-red-700";
                      default:
                        return "bg-gray-200 text-gray-700";
                    }
                  };

                  const totalAmount =
                    invoice.invoiceItems?.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) || 0;

                  return (
                    <div key={invoice.id || index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                      <div className="border-b border-gray-200 pb-2 mb-2">
                        <div className="flex justify-between items-center mb-1">
                          <h5 className="font-medium text-sm">{invoice.referenceCode || `INV-${invoice.id}`}</h5>
                          <span
                            className={`inline-block px-2 py-0.5 rounded-full font-medium text-xs ${getStatusColor(invoice.status)}`}
                          >
                            {invoice.status || "PENDING"}
                          </span>
                        </div>
                        <p className="text-gray-600 text-xs mb-1">{invoice.description || "Invoice"}</p>
                        <p className="text-gray-600 text-xs">
                          {moment(invoice.createdAt || invoice.serviceDate).format("DD-MMM-YYYY")}
                        </p>
                      </div>
                      <div className="flex flex-col space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 text-xs font-medium">Amount:</span>
                          <span className="font-medium text-sm">
                            {invoice.currency?.code || "KES"} {totalAmount.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-700 text-xs font-medium">Actions:</span>
                          <div className="flex space-x-2">
                            <button
                              className="text-blue-500 hover:text-blue-700 text-sm font-medium"
                              onClick={() => handleViewInvoice(invoice.id)}
                            >
                              View
                            </button>
                            <button
                              className="text-green-500 hover:text-green-700 text-sm font-medium"
                              onClick={() => openEditInvoiceModal(invoice)}
                            >
                              Edit
                            </button>
                            {invoice.status !== "CANCELLED" && (
                              <button
                                className="text-red-500 hover:text-red-700 text-sm font-medium"
                                onClick={() => handleDeleteInvoice(invoice)}
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-sm text-gray-500 bg-white rounded-lg shadow-sm border border-gray-200">
                  No invoices found
                </div>
              )}
            </div>
          </>
        )}

        {/* Pagination */}
        {Array.isArray(invoices) && invoices.length > 0 && (
          <div className="flex justify-center mt-6">
            <Pagination
              className="pagination-bar"
              currentPage={currentPage || 1}
              totalCount={totalPageSize || 0}
              pageSize={itemsPerPage || 10}
              siblingCount={1}
              onPageChange={changePage}
            />
          </div>
        )}
      </div>

      {/* Select Invoice Modal */}
      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Select Invoice</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 border-b border-gray-200 pb-4">
            <h2 className="text-xl font-medium text-gray-800">
              Total:{" "}
              <span className="text-blue-600">
                {localService.getCurrency()} {totalAmount}
              </span>
            </h2>
            <button
              type="button"
              className={`mt-4 md:mt-0 px-6 py-2 rounded-md text-sm font-medium focus:outline-none transition-colors ${
                !selectedInvoices || (selectedInvoices && selectedInvoices.length <= 0)
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
              disabled={!selectedInvoices || (selectedInvoices && selectedInvoices.length <= 0)}
              onClick={showMakePaymentPage}
            >
              Continue to Payment
            </button>
          </div>
          {renderResultForUnpaidInvoices()}
        </DialogContent>
      </Dialog>

      {/* Create Invoice Modal */}
      <Dialog open={showInvoiceModal} onOpenChange={setShowInvoiceModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {isPlatformAdmin && (
              <div className="p-4 bg-blue-50 rounded border border-blue-200">
                <label className="block mb-1 text-sm font-medium text-gray-700">
                  Assign to Customer Organization *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={organizationSearchTerm}
                    onChange={e => {
                      setOrganizationSearchTerm(e.target.value);
                      if (e.target.value.length >= 2) {
                        searchOrganizations(e.target.value);
                      } else {
                        setOrgSearchResults([]);
                      }
                    }}
                    placeholder="Search by organization name or code..."
                    className="w-full px-3 py-2 bg-white rounded-md border border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                {orgSearching && <div className="mt-2 text-sm text-blue-600">Searching organizations...</div>}
                {orgSearchResults.length > 0 && (
                  <div className="mt-2 max-h-48 overflow-y-auto border border-blue-200 rounded-md bg-white">
                    {orgSearchResults.map(org => (
                      <div
                        key={org.id}
                        onClick={() => {
                          setSelectedOrganization(org);
                          setInvoiceForm(prev => ({
                            ...prev,
                            organizationId: org.id,
                          }));
                          setOrganizationSearchTerm("");
                          setOrgSearchResults([]);
                        }}
                        className="p-2 cursor-pointer hover:bg-blue-50 border-b last:border-b-0"
                      >
                        <div className="font-medium text-sm">{org.name}</div>
                        <div className="text-xs text-gray-600">
                          {org.code} • {org.contactEmail || "No email"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedOrganization && (
                  <div className="mt-2 p-2 bg-blue-100 rounded border border-blue-200 text-sm">
                    Selected: <span className="font-medium">{selectedOrganization.name}</span> (
                    {selectedOrganization.code})
                    <button
                      onClick={() => {
                        setSelectedOrganization(null);
                        setInvoiceForm(prev => ({
                          ...prev,
                          organizationId: undefined,
                        }));
                      }}
                      className="ml-2 text-blue-700 hover:underline"
                    >
                      change
                    </button>
                  </div>
                )}
              </div>
            )}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Currency</label>
              <select
                value={invoiceForm.currencyId}
                onChange={e =>
                  setInvoiceForm({
                    ...invoiceForm,
                    currencyId: Number(e.target.value),
                  })
                }
                className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {currencies.map(cur => (
                  <option key={cur.id} value={cur.id}>
                    {cur.code} ({cur.symbol}) - {cur.name}
                    {cur.isDefault ? " • default" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Description</label>
              <input
                type="text"
                value={invoiceForm.description}
                onChange={e =>
                  setInvoiceForm({
                    ...invoiceForm,
                    description: e.target.value,
                  })
                }
                className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter invoice description"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Service Date</label>
              <input
                type="date"
                value={invoiceForm.serviceDate}
                onChange={e =>
                  setInvoiceForm({
                    ...invoiceForm,
                    serviceDate: e.target.value,
                  })
                }
                className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Product Type</label>
              <select
                value={invoiceForm.productType}
                onChange={e =>
                  setInvoiceForm({
                    ...invoiceForm,
                    productType: e.target.value,
                  })
                }
                className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="SUBSCRIPTION">Subscription</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Invoice Items</label>
              {invoiceForm.invoiceItems.map((item, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={item.description}
                    onChange={e => updateInvoiceItem(index, "description", e.target.value)}
                    className="flex-1 px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Item description"
                  />
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={e => updateInvoiceItem(index, "quantity", e.target.value)}
                    className="w-24 px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Qty"
                  />
                  <input
                    type="number"
                    step="0.01"
                    value={item.unitPrice}
                    onChange={e => updateInvoiceItem(index, "unitPrice", e.target.value)}
                    className="w-32 px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Unit Price"
                  />
                  {invoiceForm.invoiceItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeInvoiceItem(index)}
                      className="px-3 py-2 text-red-600 hover:text-red-800"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addInvoiceItem}
                className="mt-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
              >
                <FaPlus className="inline-block mr-1 w-3 h-3" />
                Add Item
              </button>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowInvoiceModal(false)}
              className="px-4 py-2 text-gray-600 rounded-md border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateInvoice}
              className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Create Invoice
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Invoice Modal */}
      <Dialog open={showEditInvoiceModal} onOpenChange={setShowEditInvoiceModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={invoiceForm.description}
                onChange={e => setInvoiceForm({ ...invoiceForm, description: e.target.value })}
                className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Enter invoice description"
              ></textarea>
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={() => setShowEditInvoiceModal(false)}
              className="px-4 py-2 text-gray-600 rounded-md border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateInvoice}
              className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
            >
              Update Invoice
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Details Modal */}
      <Dialog open={showInvoiceDetailsModal} onOpenChange={setShowInvoiceDetailsModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          {detailsLoading ? (
            <div className="py-6">
              <LoadingSpinner loading={true} />
            </div>
          ) : invoiceDetails ? (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Reference</span>
                <span className="font-medium">{invoiceDetails.referenceCode || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Description</span>
                <span className="font-medium text-right">{invoiceDetails.description || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Amount</span>
                <span className="font-medium">
                  {(() => {
                    const cur = currencies.find(c => c.id === invoiceDetails.currencyId);
                    const code = cur?.code || "";
                    return `${code} ${invoiceDetails.amount || "-"}`;
                  })()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status</span>
                <span className="font-medium">{invoiceDetails.status || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Service Date</span>
                <span className="font-medium">
                  {invoiceDetails.serviceDate ? moment(invoiceDetails.serviceDate).format("DD-MMM-YYYY") : "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Created</span>
                <span className="font-medium">
                  {invoiceDetails.createdAt ? moment(invoiceDetails.createdAt).format("DD-MMM-YYYY") : "-"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Product Type</span>
                <span className="font-medium">{invoiceDetails.productType || "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Organization</span>
                <span className="font-medium">{invoiceDetails.organizationId ?? "-"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Reference ID</span>
                <span className="font-medium">{invoiceDetails.id}</span>
              </div>
            </div>
          ) : (
            <div className="py-4 text-sm text-gray-500">No details available</div>
          )}
        </DialogContent>
      </Dialog>

      {/* No Mobile Wallet Modal */}
      <BlockerModal
        title="Sorry"
        isOpen={hasNoMobileWalletAccount}
        isClose={() => setHasNoMobileWalletAccount(false)}
        onSubmit={() => history.push("/settings/momopayment")}
        doneButton="Account Setup"
      >
        <div className="text-center">
          <p className="text-gray-600 font-normal mb-4">
            Whoa! You do not have a mobile wallet account set up. Please click on the button below to set up a mobile
            wallet account
          </p>
          <img src={warning} alt="warning" className="mx-auto w-16 h-16" />
        </div>
      </BlockerModal>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Invoice</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this invoice? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={confirmDeleteInvoice} className="bg-blue-600 hover:bg-blue-700">
              Cancel Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Orders;
